<template>
  <DialogTwoColFullscreen
    v-model="showAreaCreateDialog"
    @dialogCancel="resetStandardAreaTool"
  >
    <template #title>Create Area</template>

    <!-- left column -->
    <template #left-col>
      <!-- inner content -->
      <div class="header"><h3>Standard Areas</h3></div>
      <v-divider></v-divider>
      <div class="two-col-wrapper">
        <BaseTwoColLayout :leftFr="1" :rightFr="1" :gap="0">
          <!-- left panel -->
          <template #left>
            <!-- Areas Search and Lists -->
            <AreaListStandard></AreaListStandard>
          </template>
          <!-- right panel -->
          <template #right>
            <div
              class="w-100 h-100 justify-center d-flex align-center border-s-sm"
            >
              <MapComponent
                ref="map"
                :initialiseMapOnMount="false"
                :showMapControls="{
                  zoomControl: false,
                  disableDefaultUI: true,
                }"
                height="100%"
                width="100%"
                :loading="loadingAreaGeometry"
              />
            </div>
          </template>
        </BaseTwoColLayout>
      </div>
    </template>

    <!-- right column -->
    <template #right-col>
      <AreaSelectedAreas
        class="one-col-wrapper"
        @refreshCategories="() => $emit('refreshCategories')"
      ></AreaSelectedAreas>
    </template>

    <!--footer content -->
    <template #footer-left>
      <v-btn
        variant="text"
        @click="$emit('update:modelValue', false)"
        rounded="0"
      >
        Close
      </v-btn>
    </template>
    <template #footer-center>
      <div class="text-caption text-center w-50">
        <p class="text-grey">
          Source: Office for National Statistics licensed under the Open
          Government Licence v.3.0
        </p>
        <p class="text-grey">
          Contains OS data © Crown copyright and database right
          {{ new Date().getFullYear() }}
        </p>
      </div>
    </template>
    <template #footer-right>
      <v-btn
        color="success"
        variant="elevated"
        rounded="0"
        @click="saveAreas"
        :disabled="saveAreaBtnDisabled"
        :loading="savingCustomAreas == true"
      >
        Save Area
      </v-btn>
    </template>
  </DialogTwoColFullscreen>
</template>

<script>
import DialogTwoColFullscreen from "@/components/DialogTwoColFullscreen.vue";
import BaseTwoColLayout from "@/components/BaseTwoColLayout.vue";
import AreaListStandard from "@/components/AreaListStandard";
import AreaSelectedAreas from "@/components/AreaSelectedAreas.vue";

import { systemMessages } from "@/mixins/SystemMessages";

import { createNamespacedHelpers } from "vuex";
const { mapState, mapMutations, mapGetters } =
  createNamespacedHelpers("customAreaManager");
import MapComponent from "@/components/Map.vue";

export default {
  name: "AreaCreateStandard",
  components: {
    DialogTwoColFullscreen,
    BaseTwoColLayout,
    AreaListStandard,
    AreaSelectedAreas,
    MapComponent,
  },
  mixins: [systemMessages],
  computed: {
    //states from VUEX to read values e.g. this.standardAreas
    ...mapState([
      "selectedAreaIds",
      "customAreaName",
      "customAreaDescription",
      "selectedCategoryId",
      "selectedDataLevelTimepoint",
      "singleCustomArea",
      "customAreaNameOverrides",
      "savingCustomAreas",
    ]),
    //getters from VUEX to read derived values
    ...mapGetters(["selectedAreaData"]),
    saveAreaBtnDisabled() {
      //save as multiple cusom areas
      if (!this.singleCustomArea) {
        return this.selectedAreaIds.length === 0;
      }

      //save as single custom area
      return this.selectedAreaIds.length === 0 || this.customAreaName === "";
    },
    showAreaCreateDialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  data() {
    return {
      formErrors: {},
      loadingAreaGeometry: false,
    };
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
  },
  mounted() {},
  methods: {
    //mutations from VUEX to set state values e.g. this.setStandardAreas(value)
    ...mapMutations([
      "setSavingCustomAreas",
      "setSelectedAreaDataLevelId",
      "setSelectedDataLevelTimepoint",
      "removeSelectedAreaIds",
      "clearSelectedAreaIds",
      "setSelectedCategoryId",
      "setCustomAreaName",
      "clearAllCustomAreaNameOverrides",
      "setCustomAreaDescription",
      "setSingleCustomArea",
      "setFormErrors",
      "setErrorAreaIds",
      "setErrorAreaReasons",
      "clearErrorAreas",
    ]),
    /** build array of custom area data to save to backend (for single or multiple custom areas) */
    BuildAreaData() {
      const customAreas = [];

      //common area attributes
      let areaData = {
        custom_area_category_id: this.selectedCategoryId,
        timepoint: this.selectedDataLevelTimepoint,
        type_id: 1, //standard area
      };

      //single custom area with multiple area ids
      if (this.singleCustomArea) {
        areaData = {
          ...areaData,
          name: this.customAreaName,
          description: this.customAreaDescription,
          area_ids: this.selectedAreaIds,
        };

        customAreas.push(areaData);
      } else {
        //multiple custom areas - one per selected standard area
        this.selectedAreaData.forEach((area) => {
          const name = this.customAreaNameOverrides[area.id] || area.area_name;
          const area_ids = [area.id];
          areaData = {
            ...areaData,
            name: name,
            area_ids: area_ids,
          };

          customAreas.push(areaData);
        });
      }

      return customAreas;
    },
    /** save custom area(s) to backend */
    async saveAreas() {
      this.setSavingCustomAreas(true);
      this.setFormErrors({});
      this.clearErrorAreas();

      const areaData = this.BuildAreaData();

      // Add index to each area for bulk tracking
      const areaDataWithIndex = areaData.map((area, index) => ({
        ...area,
        index: index,
      }));

      let success = [];
      let error = [];

      try {
        // Single bulk API call instead of multiple individual calls
        const bulkResponse = await this.$axios.post(
          "/bulk-custom-areas-optimized",
          areaDataWithIndex,
        );

        // Transform bulk response to Promise.allSettled format for compatibility
        const results = bulkResponse.data.map((item, idx) => {
          if (item.saved) {
            return {
              status: "fulfilled",
              value: {
                status: 200,
                config: { data: JSON.stringify(areaDataWithIndex[idx]) },
              },
            };
          } else {
            return {
              status: "rejected",
              reason: {
                message: item.saved_message,
                response: {
                  data: {
                    message: item.saved_message,
                    errors: { name: [item.saved_message] },
                  },
                },
                config: { data: JSON.stringify(areaDataWithIndex[idx]) },
              },
            };
          }
        });

        results.forEach((result) => {
          if (result.status === "fulfilled" && result.value.status < 400) {
            success.push(result);
          } else {
            error.push(result);
          }
        });
      } catch (axiosError) {
        // Handle network errors or other API failures
        this.setSavingCustomAreas(false);
        this.errorMsg({
          message: axiosError.message || "Failed to save custom areas",
          title: "Error! Failed to save custom areas",
        });
        return;
      }

      this.setSavingCustomAreas(false);

      //if save was completely successful reset standard area tool
      if (success.length > 0 && error.length == 0) {
        this.successPill({
          icon: "mdi-content-save",
          title: "Custom area(s) saved",
        });

        this.resetStandardAreaTool();
        this.showAreaCreateDialog = false;

        // Refresh custom areas table
        this.$emit("getCustomAreasByCategory");
        return;
      }

      //there were errors - handle
      if (error.length > 0) {
        // single custom area
        if (this.singleCustomArea) {
          error.forEach((e) => {
            const formErrors = e.reason?.response?.data?.errors || null;
            if (!formErrors) {
              //generic error
              this.errorMsg({
                message: e.reason.message,
                title: "Error! Failed to save custom area",
              });
              return;
            }

            //form errors
            this.setFormErrors(formErrors);
            this.errorPill({
              icon: "mdi-alert-circle",
              title: "Custom area could not be saved",
              timeout: 4000,
            });
          });
        } else {
          //multiple custom areas
          this.highlightMultipleCustomAreaErrors(success, error);
          this.warningPill({
            icon: "mdi-alert-circle",
            title: "Some custom areas could not be saved",
            timeout: 4000,
          });
        }
      }
      // console.log({ success: success });
      // console.log({ error: error });
    },
    /** reset standard area tool */
    resetStandardAreaTool() {
      this.setSelectedAreaDataLevelId(null);
      this.setSelectedDataLevelTimepoint(null);
      this.clearSelectedAreaIds();
      this.setSelectedCategoryId(null);
      this.setCustomAreaName("");
      this.clearAllCustomAreaNameOverrides();
      this.setCustomAreaDescription("");
      this.setSingleCustomArea(true);
      this.clearErrorAreas();
    },
    /** update selected areas to highlight multple custom area error */
    highlightMultipleCustomAreaErrors(success, error) {
      //inner function to extract area_ids from promise result
      const extractIdsFromAxios = (r) => {
        //fulfilled = r.result.value, rejected = r.result.reason
        const cfg = r.value?.config ?? r.reason?.config;
        //return empty array if there is no data
        if (!cfg?.data) return [];
        try {
          const payload =
            typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data;
          const ids = Array.isArray(payload?.area_ids) ? payload.area_ids : [];

          //normalise strings to match selectedAreaIds string keys
          return ids.map(String);
        } catch {
          return [];
        }
      };

      //get ids for success/error saved areas
      const successIds = new Set(success.flatMap(extractIdsFromAxios));
      const errorIds = new Set(error.flatMap(extractIdsFromAxios));

      //get error reason
      const errorReasonsById = {};
      // disassemble Laravel errorbag type list of validation issues
      error.forEach((e) => {
        const reason = e.reason.response.data;
        const list =
          reason?.errors && typeof reason.errors === "object"
            ? Object.entries(reason.errors).flatMap(([field, msgs]) =>
                Array.isArray(msgs)
                  ? msgs
                  : [String(msgs)].map((m) => `${field}: ${m}`),
              )
            : reason.message;
        //add comma separated list of reasons to error object
        errorReasonsById[String(JSON.parse(e.reason.config.data).area_ids[0])] =
          list.join(", ");
      });

      //remove successfully saved areas from list
      this.removeSelectedAreaIds([...successIds]);

      //set error Ids for user feedback in list
      this.setErrorAreaIds([...errorIds]);

      //set error reasons for user feedback
      this.setErrorAreaReasons(errorReasonsById);
    },
  },
  watch: {
    showAreaCreateDialog(open) {
      // initialise map
      this.$nextTick(() => {
        if (open) {
          this.$refs.map.mapSetUp();
        } else {
          this.$refs.map.killMap();
        }
      });
    },
    selectedAreaIds: {
      async handler(newVal, oldVal) {
        this.loadingAreaGeometry = true;

        if (newVal.length > oldVal.length) {
          // New areas were added
          await this.$refs.map.displayStandardAreas(newVal);
        } else {
          // Some areas were removed
          const removedAreas = oldVal.filter((id) => !newVal.includes(id));
          await this.$refs.map.hideStandardAreas(removedAreas);
        }

        // Always zoom to fit all currently selected areas
        if (newVal.length > 0) {
          this.$refs.map.zoomInStandardAreas(newVal);
        }

        this.loadingAreaGeometry = false;
      },
    },
  },
};
</script>

<style scoped>
.one-col-wrapper,
.two-col-wrapper {
  flex: 1 1 auto;
  min-height: 0;
}
.header {
  flex: 0 0 auto; /* <- do NOT shrink */
  display: flex; /* nicer vertical centering */
  align-items: center;
  height: 54px;
  padding: 4px 16px;
  background-color: #f9f9f9;

  h3 {
    font-weight: 600;
    color: #4d4d4d;
  }
}
</style>
